/**
 * Human words for a task’s closed sets — shared by the board card and the forms.
 *
 * Kept in a leaf module so `actionCopy` can reuse them without importing
 * `TaskCardFull` (which pulls in `ActionDialog` → `ActionForm` → `actionCopy`).
 */

import {
  isBefore,
  PREPARED_ACTION_KIND,
  taskMachine,
  type TaskEvent,
  type TaskOutput,
  type TaskView,
} from '@fareast/shared'

import { changedByOthers } from '../../api/problem.js'

/**
 * `finance` is "Billing" because that is the word `studio/tasks.html` puts on
 * TuncLaw's declined-card task and Vela's SEPA one; the queue is named for who works
 * it and the card is named for what it is about.
 */
export const QUEUE_WORD: Readonly<Record<TaskView['queue'], string>> = {
  delivery: 'Delivery',
  content: 'Content',
  seo: 'SEO',
  odoo: 'Odoo',
  reports: 'Reports',
  account: 'Account',
  finance: 'Billing',
}

/** The state words, as the detail page and the list table print them. */
export const STATUS_WORD: Readonly<Record<TaskView['status'], string>> = {
  open: 'Open',
  claimed: 'Claimed',
  in_progress: 'In progress',
  blocked: 'Blocked',
  review: 'Waiting for a person',
  done: 'Done',
  cancelled: 'Cancelled',
}

/** Qualifier beside an agent name — not a colleague, so not drawn as a face. */
export const AGENT_NOUN = 'an agent'

/**
 * The five priority bands, as words — the owner's own request (17 Sep): "make
 * priority into text instead of numbers. High, medium, low, urgent".
 *
 * He named four words for five bands, and the scale is not ours to shorten: it is
 * `TaskPrioritySchema`'s 1–5, a CHECK constraint on the column, and the order
 * `claim_next` hands work out in. So the numbers keep running the queue and only the
 * reading changes; "Lowest" is the fifth word and is the one thing here to confirm
 * with him. 1 is the most urgent, 3 is what a task gets when nobody says.
 */
export const PRIORITY_WORD: Readonly<Record<string, string>> = {
  '1': 'Urgent',
  '2': 'High',
  '3': 'Medium',
  '4': 'Low',
  '5': 'Lowest',
}

/**
 * One band as a word, or the bare number if a row ever carries one the scale does
 * not name. A task the database let through is still a task the board must draw.
 */
export function priorityWord(priority: number): string {
  return PRIORITY_WORD[String(priority)] ?? String(priority)
}

/**
 * Whether this reader may make a *work* move on a task — claim, start, block,
 * complete, give it back.
 *
 * Nobody's task is everybody's; a task with a holder is that holder's. It lives in
 * this leaf rather than in `taskDetail/Lifecycle` because the list row, the board
 * card, the task page and the More menu all ask it, and the menu importing the page
 * that draws it was a cycle.
 */
export function canMutateWorkOf(task: TaskView, userId: string | null): boolean {
  if (task.assignee_id === null) return true
  if (userId === null) return true
  return task.assignee_id === userId
}

/** The three facts a move, or a hand-over, is made from. */
export type HeldAs = Pick<TaskView, 'assignee_kind' | 'assignee_id' | 'status'>

/** Their names, for whoever has to ask whether a condition is this one (`ActionForm`). */
export const HELD_FIELDS = [
  'assignee_kind',
  'assignee_id',
  'status',
] as const satisfies readonly (keyof HeldAs)[]

/**
 * Who holds a task and where it stands, **as the page showing the control has
 * them** — what a hand-over and every move on one task send as `if_was`.
 *
 * `canMutateWorkOf` above decides which moves a reader is offered, and it decides
 * from a row that is up to a minute old (`api/hooks.ts` `LIVE_REFETCH_MS`) and
 * older under an open window. The API lets any studio person move any task — that
 * is how a stuck one is unstuck — so between the two a move pressed on "mine" landed
 * on a task a manager had just given to a colleague: *Give it back* put theirs on
 * the queue, *Start* started it under their name. Sent with what the row said, the
 * move is refused instead (`apps/api` `tasks/service.ts` `assertMovedFrom`) and the
 * reader is told whose it is now.
 *
 * One function because it was three literals: the Assign window, the row's take,
 * and — the day the moves learnt it — eleven more.
 */
export function heldAs(task: HeldAs): HeldAs {
  return { assignee_kind: task.assignee_kind, assignee_id: task.assignee_id, status: task.status }
}

/**
 * What every move on **one** task starts with: the task, and what the move is made
 * from. A window's `fixed`, or the whole body of a move that asks nothing else.
 *
 * Not for `tasks_claim`, which takes no condition and needs none — a task somebody
 * else has been given or has taken is refused by the claim itself. A batch
 * (`pages/studio/tasks/BulkBar.tsx`) says the same of each of its rows (`heldAs`).
 */
export function moveOf(task: HeldAs & Pick<TaskView, 'ref'>): {
  readonly task_ref: string
  readonly if_was: HeldAs
} {
  return { task_ref: task.ref, if_was: heldAs(task) }
}

/**
 * A window's two props for **a move on one task**: what the move is made from
 * (`moveOf`), and the stamp of the row that said so.
 *
 * Together, and from one place, because the condition alone cannot tell an answer
 * that is news from one the row had already shown (`ActionForm` `useOpenedFrom`):
 * a window opened over a row that had been read again *after* the reader's own move
 * was put back to that move's answer when it landed — older than what the reader was
 * looking at — and refused. Spread into the window: `{...moveWindowOf(task)}`.
 */
export function moveWindowOf(task: HeldAs & Pick<TaskView, 'ref' | 'updated_at'>): {
  readonly fixed: ReturnType<typeof moveOf>
  readonly stamp: string
} {
  return { fixed: moveOf(task), stamp: task.updated_at }
}

/**
 * The same for a window that sets **one field** on a task, which is made from that
 * field's own value — the bulk bar's Due and Queue windows.
 */
export function editWindowOf<Field extends 'due_at' | 'queue'>(
  task: Pick<TaskView, 'ref' | 'updated_at' | Field>,
  field: Field,
): {
  readonly fixed: Readonly<Record<string, unknown>>
  readonly stamp: string
} {
  return {
    fixed: { task_ref: task.ref, if_was: { [field]: task[field] } },
    stamp: task.updated_at,
  }
}

/**
 * Whether a write's answer is **news** to somebody who has seen the task as of
 * `seen` — both are the task's `updated_at`, which is its version
 * (`CellEdit.tsx` `stampOf`).
 *
 * A write of the reader's own is "landed" only after the page has been read again,
 * and the reads come back one at a time: the row can show that write, *and what a
 * colleague did after it*, while the write is still waiting for the others. Something
 * opened over that row has seen more than the answer says, and the answer must not be
 * what it remembers. Not news unless it is strictly later; and where either stamp is
 * missing or is not an instant, nobody can say, and it is taken for news as it was
 * before there were stamps to compare.
 */
export function isNewsSince(seen: string | null | undefined, answered: unknown): boolean {
  if (typeof seen !== 'string' || typeof answered !== 'string') return true
  const [was, now] = [new Date(seen), new Date(answered)]
  if (Number.isNaN(was.getTime()) || Number.isNaN(now.getTime())) return true
  return isBefore(was, now)
}

/**
 * A task's fields as a sentence names them, for the one sentence that has to: which
 * of them somebody else changed first. Two of the published names are one thing to a
 * reader — the holder is a kind and an id — and say so once.
 */
const FIELD_WORD: Readonly<Record<string, string>> = {
  title: 'the title',
  instructions: 'the notes',
  acceptance: 'the done-when',
  queue: 'the queue',
  priority: 'the priority',
  due_at: 'the due date',
  needs_review: 'whether it needs review',
  service_code: 'the service',
  money: 'the money',
  money_ref: 'the money reference',
  assignee_kind: 'who holds it',
  assignee_id: 'who holds it',
  status: 'where it stands',
}

/**
 * What the page's line says when a change made from a row was not made because
 * **somebody else changed the same thing first** — or null when the refusal is not
 * that one, and the caller says whatever it would have said.
 *
 * A row is at most a minute old (`api/hooks.ts` `LIVE_REFETCH_MS`), and a change
 * made from it says what it was made from (`if_was`): a priority picked over one a
 * colleague had just set, a task given to the reader after somebody else claimed it.
 * The API refuses those rather than letting the later click win unseen, and its own
 * sentence tells a tool caller to read the task again. The page has already done
 * that — a refusal re-reads the module — so this says what happened and where to
 * look: the row is showing their value now.
 *
 * `made` is what was refused, because the two end differently: an **edit** was a
 * value, which was not saved and can be typed again; a **move** was a thing to do,
 * which was not done, and whether it still should be is a question about the task
 * as it now stands.
 */
export function overtaken(
  taskRef: string,
  problem: unknown,
  made: 'edit' | 'move' = 'edit',
): string | null {
  const what = changedWords(problem)
  if (what === null) return null
  const said = `Somebody else changed ${what} on ${taskRef} just before you did`
  return made === 'move'
    ? `${said}, so nothing was done. The task shows theirs now — do it from there if you still want to.`
    : `${said}, so yours was not saved. The task shows theirs now — change it again if you still want to.`
}

/** True for a write about this task, by what it sent. */
export function isAboutTask(sent: unknown, taskRef: string): boolean {
  return (
    typeof sent === 'object' &&
    sent !== null &&
    (sent as { readonly task_ref?: unknown }).task_ref === taskRef
  )
}

/**
 * Which of a task's fields a refusal says somebody else changed first, as a sentence
 * names them — "who holds it and where it stands" — or null when it is not that
 * refusal. The line above is built on it, and so is the foot of a window
 * (`ActionForm`), which says the same fact where the reader pressed.
 */
export function changedWords(problem: unknown): string | null {
  const changed = changedByOthers(problem)
  return changed === null ? null : wordsFor(changed)
}

/** A task's fields by their published names, as one phrase a sentence can carry. */
export function wordsFor(names: readonly string[]): string {
  const words = [...new Set(names.map((name) => FIELD_WORD[name] ?? name))]
  const last = words.pop() ?? 'it'
  return words.length === 0 ? last : `${words.join(', ')} and ${last}`
}

/** The call an agent prepared and a task is holding for a person, or null when it carries none. */
export function preparedActionOf(
  task: TaskView,
): Extract<TaskOutput, { kind: typeof PREPARED_ACTION_KIND }> | null {
  return task.outputs.find((one) => one.kind === PREPARED_ACTION_KIND) ?? null
}

/**
 * True while a task is holding a call **nobody has made** — which is when closing it
 * from a row is the wrong move.
 *
 * A held call is made from its task while the task is in review, and from nowhere
 * else afterwards (`apps/api` `tasks/service.ts` `heldCallFor`), so "Approve and
 * close" on such a row does not approve the spend: it ends the one state the spend
 * could be made from, with nothing on a list row to say so. The task page is where
 * the arguments are read and the call is made, and where closing without making it
 * is a decision somebody can see themselves taking.
 *
 * One predicate because two controls ask it — the row's button and the row's Status
 * cell — and the day only one of them did, the cell offered what the button beside
 * it refused. Once the call has run (`ran_at`), closing is exactly what is left to
 * do, and both offer it.
 */
export function holdsUnmadeCall(task: TaskView): boolean {
  const held = preparedActionOf(task)
  return held !== null && held.ran_at === undefined
}

/**
 * What to ask before Unassigned takes somebody off live work (#48): the server puts a
 * claimed or in-progress task back on the queue. Null when nobody is on any of them.
 */
export function unassignQuestion(
  tasks: readonly Pick<TaskView, 'ref' | 'status'>[],
): { readonly title: string; readonly says: string; readonly confirmLabel: string } | null {
  // The server's own rule (`assign()`): a task the machine can release goes back on the queue.
  const held = tasks.filter((task) => taskMachine.can(task.status, 'release'))
  const first = held[0]
  if (first === undefined) return null
  if (tasks.length === 1) {
    return {
      title: `Unassign ${first.ref}?`,
      says: `${first.ref} has somebody on it. Unassigning it puts it back on the queue, and nobody is on it after this.`,
      confirmLabel: 'Unassign and return to queue',
    }
  }
  const refs = held.map((task) => task.ref).join(', ')
  return {
    title: `Unassign ${String(tasks.length)} tasks?`,
    says:
      held.length === 1
        ? `${refs} has somebody on it and goes back to the queue. Nobody is on it after this.`
        : `${refs} have somebody on them and go back to the queue. Nobody is on them after this.`,
    confirmLabel: `Unassign ${String(tasks.length)}`,
  }
}

/** An action a move opens a dialog for, with the words that go on it. */
export type TaskMove = {
  readonly action:
    | 'tasks_block'
    | 'tasks_unblock'
    | 'tasks_submit_for_review'
    | 'tasks_complete'
    | 'tasks_release'
    | 'tasks_cancel'
  readonly label: string
  readonly title: string
}

/**
 * One dialog per event that carries more than a reference, keyed by the event the
 * machine names. An event with no entry here is not a person's move.
 *
 * `block` joined them in correction round 1: it was the one move written out twice —
 * once in `BlockControl`, once in the row's More menu — and the status cell would
 * have been a third. Its own control on the task page reads these words now.
 *
 * It lives in this leaf because the task page's lifecycle row and the More menu on a
 * row, a card and the page all draw from it — two copies of "Why is it not being
 * done?" is two answers to the same question.
 */
export const DIALOG_MOVES: Readonly<Partial<Record<TaskEvent, TaskMove>>> = {
  /*
   * "Block", not "Block…". The word is the **commit**: it is read off this table by
   * the dialog's own submit button, and a button that says "Block…" promises one
   * more question after it. The ellipsis belongs to whatever *opens* the window —
   * the menu entry and the task page's own control add it where they draw it.
   */
  block: { action: 'tasks_block', label: 'Block', title: 'Say what it is waiting for' },
  unblock: { action: 'tasks_unblock', label: 'Unblock', title: 'What arrived?' },
  submit_for_review: {
    action: 'tasks_submit_for_review',
    label: 'Send for review',
    title: 'It waits in review, and whoever raised it is told',
  },
  complete: {
    action: 'tasks_complete',
    label: 'Mark done',
    title: 'Close it now, or send it to review if it needs one',
  },
  /*
   * The same tool, and that is the point. `.docs/reports/07-report.md`: "tasks_complete
   * on a task in review is the approval, and only for a staff principal — there is no
   * tasks_approve_review tool." The label is what says so to the person pressing it.
   */
  approve_review: {
    action: 'tasks_complete',
    label: 'Approve and close',
    title: 'Close the review — the work is done',
  },
  release: { action: 'tasks_release', label: 'Give it back', title: 'Return it to the queue' },
  cancel: { action: 'tasks_cancel', label: 'Cancel it', title: 'Why is it not being done?' },
}

/**
 * What each of the two closing moves actually does, in a sentence (#25).
 *
 * The owner asked to "clarify Complete versus Send for review — explain the resulting
 * state and who receives the task". These are read off the service rather than
 * guessed: `submitForReview` moves the task to `review`, **keeps the assignee**, and
 * mails `reviewerOf` — whoever raised it, or an agent's owner, falling back to the
 * studio's own address. `complete` on a task that needs review does the same thing
 * under a different name, which is the confusion this sentence exists to end.
 */
export const CLOSING_WORDS: Readonly<Record<'submit_for_review' | 'complete', string>> = {
  submit_for_review:
    'Send for review keeps the task with you and waits: whoever raised it is told, and any studio colleague can approve and close it.',
  complete:
    'Mark done closes it outright — nobody reviews it and nobody is told. A task that needs approval goes to review instead, whichever of the two you press.',
}

/**
 * The words on a move, wherever it is offered — a row button, the task page's
 * lifecycle row, or the status menu in a list cell.
 *
 * `DIALOG_MOVES` already words the five that open a dialog; these are the two that do
 * not, and reading them from one function is what stops the status menu inventing a
 * third spelling of "Claim".
 *
 * **One move, one word**, since the owner counted them on 17 September: taking a task
 * read as Claim, Pick it up, Claim it, Assign to me and Assign to me & start, four of
 * which could be on one card at once. It is "Claim" everywhere now — the row's
 * button, the status cell's menu and the task page's lifecycle row — and the only
 * other word left in the family is "Claim & start", which is two moves and says so.
 * "Pick it up" is gone: it existed to soften Claim on a task already yours, and a
 * second word for the same call is the thing that made the page unreadable.
 */
export function moveLabel(event: TaskEvent): string {
  if (event === 'claim') return 'Claim'
  if (event === 'start') return 'Start'
  /*
   * The word that *offers* the move, which is not always the word that commits it:
   * a menu entry reading "Cancel it" beside "Close" is two dismissals, so the entry
   * names the task and the button inside the dialog keeps the verb.
   */
  if (event === 'cancel') return 'Cancel this task'
  return DIALOG_MOVES[event]?.label ?? event
}
